/**
 * Single shared microphone analyser for every Audio node in the graph.
 *
 * The rete version opened a fresh `getUserMedia` stream per node instance and
 * leaked it on delete; here one stream is opened lazily and reference-counted.
 */

export interface AudioBands {
  level: number;
  lows: number;
  mids: number;
  highs: number;
}

const SILENT: AudioBands = { level: 0, lows: 0, mids: 0, highs: 0 };

let stream: MediaStream | null = null;
let context: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let frequencies: Uint8Array<ArrayBuffer> | null = null;
let opening: Promise<void> | null = null;
let failure: string | null = null;
let consumers = 0;

/** Opens the microphone on first use. Safe to call every frame. */
export function acquireMicrophone(): void {
  consumers += 1;
  if (opening ?? failure) return;

  opening = navigator.mediaDevices
    .getUserMedia({ audio: true, video: false })
    .then((granted) => {
      stream = granted;
      context = new AudioContext();
      analyser = context.createAnalyser();
      analyser.fftSize = 256;
      frequencies = new Uint8Array(analyser.frequencyBinCount);
      context.createMediaStreamSource(granted).connect(analyser);
    })
    .catch((error: unknown) => {
      failure =
        error instanceof Error ? error.message : "Microphone unavailable";
    });
}

export function releaseMicrophone(): void {
  consumers = Math.max(0, consumers - 1);
  if (consumers > 0) return;

  for (const track of stream?.getTracks() ?? []) track.stop();
  void context?.close();

  stream = null;
  context = null;
  analyser = null;
  frequencies = null;
  opening = null;
}

export function microphoneError(): string | null {
  return failure;
}

/** Current spectrum, split into the three bands the Audio node exposes. */
export function readBands(): AudioBands {
  if (!analyser || !frequencies) return SILENT;

  // Bound to a const so the null check survives into `peak`.
  const data = frequencies;
  analyser.getByteFrequencyData(data);

  const peak = (from: number, to: number): number => {
    let max = 0;
    for (let i = from; i < Math.min(to, data.length); i += 1) {
      const value = data[i] ?? 0;
      if (value > max) max = value;
    }
    return max / 256;
  };

  return {
    level: peak(0, data.length),
    lows: peak(0, 1),
    mids: peak(1, 13),
    highs: peak(13, 40),
  };
}
